import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  listExportJobs,
  deleteExportJob,
  type ExportJob,
  ExportJobStatuses,
  DataManagementModules,
} from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';
import DataManagementNav from './DataManagementNav';

const statusColors: Record<string, string> = {
  pending: 'default',
  exporting: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function ExportJobListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [moduleFilter, setModuleFilter] = useState<string | undefined>();

  const canRead = hasPermission('data_management', 'read');
  const canWrite = hasPermission('data_management', 'write');
  const canDelete = hasPermission('data_management', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: Record<string, any> = { ...params };
      if (statusFilter) filters.status = statusFilter;
      if (moduleFilter) filters.source_module = moduleFilter;
      return listExportJobs(filters);
    },
    [statusFilter, moduleFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('data_management.export.delete_confirm'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteExportJob(id);
            message.success(t('data_management.export.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<ExportJob> = useMemo(
    () => [
      {
        title: t('data_management.export.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('data_management.export.field.source_module'),
        dataIndex: 'source_module',
        key: 'source_module',
        width: 120,
        render: (v: string) => t(`data_management.import.modules.${v}`, v),
      },
      {
        title: t('data_management.export.field.file_format'),
        dataIndex: 'file_format',
        key: 'file_format',
        width: 80,
        render: (v: string) => t(`data_management.import.formats.${v}`, v).toUpperCase(),
      },
      {
        title: t('data_management.export.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (v: string) => (
          <Tag color={statusColors[v] ?? 'default'}>
            {t(`data_management.export.statuses.${v}`, v)}
          </Tag>
        ),
      },
      {
        title: t('data_management.export.field.total_rows'),
        dataIndex: 'total_rows',
        key: 'total_rows',
        width: 100,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.export.field.exported_rows'),
        dataIndex: 'exported_rows',
        key: 'exported_rows',
        width: 100,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('common.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        sorter: true,
        render: (v: string) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 140,
        render: (_: unknown, record: ExportJob) => (
          <Space>
            {canRead && (
              <Tooltip title={t('app.view')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/data-management/exports/${record.id}`)}
                />
              </Tooltip>
            )}
            {canWrite && record.status === 'pending' && (
              <Tooltip title={t('app.edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/data-management/exports/${record.id}/edit`)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('app.delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canRead, canWrite, canDelete, navigate, handleDelete]
  );

  return (
    <div>
      <DataManagementNav />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('data_management.export.field.status')}
          style={{ width: 140 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={ExportJobStatuses.map((s) => ({
            value: s,
            label: t(`data_management.export.statuses.${s}`),
          }))}
        />
        <Select
          allowClear
          placeholder={t('data_management.export.field.source_module')}
          style={{ width: 140 }}
          value={moduleFilter}
          onChange={setModuleFilter}
          options={DataManagementModules.map((m) => ({
            value: m,
            label: t(`data_management.import.modules.${m}`),
          }))}
        />
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/data-management/exports/new')}
          >
            {t('data_management.export.new')}
          </Button>
        )}
      </Space>
      <DataGrid
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />
    </div>
  );
}
